import { useCallback } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import type { HomeyFlowSnap } from "@/types/homey";

/**
 * Flow triggering + favorite toggling for the Flows sub-view.
 *
 * - `trigger` returns the mutateAsync promise so FlowButton can drive its
 *   running/success/error state machine off resolve/reject.
 * - `toggleFavorite` optimistically patches the dashboard_settings cache via
 *   utils.setData, then fires the mutation; on error it invalidates to re-sync
 *   and surfaces a toast.
 */
export function useFlowActions() {
  const utils = trpc.useUtils();

  const triggerMutation = trpc.homey.triggerFlow.useMutation();

  const favoriteMutation = trpc.homey.setFavoriteFlows.useMutation({
    onError(err) {
      toast.error(`Could not update favorites: ${err.message}`);
    },
    onSettled() {
      // Re-sync from the server: confirms the optimistic patch and, when the
      // settings row did not exist yet, picks up the freshly created row.
      void utils.homey.getDashboardSettings.invalidate();
    },
  });

  /** Trigger a flow; returns a promise the FlowButton awaits. */
  const trigger = useCallback(
    (flow: Pick<HomeyFlowSnap, "id" | "type">): Promise<void> =>
      triggerMutation.mutateAsync({ id: flow.id, type: flow.type }).then(() => undefined),
    [triggerMutation],
  );

  /** Add/remove a flow id from favorites (optimistic). */
  const toggleFavorite = useCallback(
    (flowId: string) => {
      const current = utils.homey.getDashboardSettings.getData();
      const currentIds = current?.favorite_flow_ids ?? [];
      const nextIds = currentIds.includes(flowId)
        ? currentIds.filter((id) => id !== flowId)
        : [...currentIds, flowId];

      // Optimistic cache patch so the star + Favorites group update instantly.
      utils.homey.getDashboardSettings.setData(undefined, (old) =>
        old ? { ...old, favorite_flow_ids: nextIds } : old,
      );

      favoriteMutation.mutate({ flowIds: nextIds });
    },
    [utils, favoriteMutation],
  );

  return { trigger, toggleFavorite };
}
